import { create } from "zustand";
import { persist } from "zustand/middleware";
import { appointmentsApi } from "@services/api/appointments";
import {
  Appointment,
  AppointmentCategory,
  CategoryConfig,
  WorkshopAvailability,
} from "../types/garage/planning";
import type { Org, OrgReception } from "../types/org";
import { DEFAULT_RECEPTIONS } from "../types/org";

function receptionToCategory(r: OrgReception): CategoryConfig {
  return { id: r.id, label: r.label, color: r.color, bgColor: r.bgColor, borderColor: r.borderColor, barColor: r.barColor };
}

export function getCategories(org?: Org | null): CategoryConfig[] {
  const receptions = org?.receptions ?? DEFAULT_RECEPTIONS;
  return receptions.map(receptionToCategory);
}

/** @deprecated Use getCategories(org) instead */
export const CATEGORIES: CategoryConfig[] = getCategories();

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Get Mon-Fri date range for the week containing dateStr */
function getWeekRange(dateStr: string): { date_from: string; date_to: string } {
  const d = new Date(dateStr);
  const day = d.getDay(); // 0=Sun
  const diffToMon = day === 0 ? -6 : 1 - day;
  const mon = new Date(d);
  mon.setDate(d.getDate() + diffToMon);
  const fri = new Date(mon);
  fri.setDate(mon.getDate() + 4);
  return { date_from: toISO(mon), date_to: toISO(fri) };
}

export type CompactionMode = "compact" | "normal" | "spread";

export const COMPACTION_CONFIG: Record<CompactionMode, { hourBlockHeight: number; snapPx: number }> = {
  compact:  { hourBlockHeight: 80,  snapPx: 10 },
  normal:   { hourBlockHeight: 128, snapPx: 16 },
  spread:   { hourBlockHeight: 192, snapPx: 24 },
};

interface PlanningState {
  selectedDate: string;
  viewMode: "jour" | "semaine";
  compaction: CompactionMode;
  activeCategories: AppointmentCategory[];
  appointments: Appointment[];
  workshopAvailability: WorkshopAvailability[];
  isLoading: boolean;
  isRefreshing: boolean;
  searchQuery: string;
  highlightOrdreId: string | null;
  _fetchedOrgId: string | undefined;
  _loadedRange: { date_from: string; date_to: string } | undefined;

  fetchAppointments: (orgId: string, force?: boolean) => Promise<void>;
  setSelectedDate: (date: string) => void;
  setViewMode: (mode: "jour" | "semaine") => void;
  setCompaction: (mode: CompactionMode) => void;
  toggleCategory: (cat: AppointmentCategory) => void;
  setSearchQuery: (query: string) => void;
  setHighlightOrdreId: (id: string | null) => void;
  getFilteredAppointments: () => Appointment[];
  updateAppointment: (id: string, updates: Partial<Pick<Appointment, "startHour" | "durationHours" | "category" | "date">>) => void;
  createAppointment: (data: Omit<Appointment, "id"> & { org_id: string }) => Promise<Appointment | null>;
}

export const usePlanningStore = create<PlanningState>()(
  persist(
    (set, get) => ({
  selectedDate: todayIso(),
  viewMode: "jour",
  compaction: "normal" as CompactionMode,
  activeCategories: DEFAULT_RECEPTIONS.map((r) => r.id),
  appointments: [],
  workshopAvailability: [],
  isLoading: false,
  isRefreshing: false,
  searchQuery: "",
  highlightOrdreId: null,
  _fetchedOrgId: undefined,
  _loadedRange: undefined,

  fetchAppointments: async (orgId: string, force?: boolean) => {
    const state = get();
    if (state.isLoading || state.isRefreshing) return;
    const range = getWeekRange(state.selectedDate);
    // Skip if we already have this week's data (unless forced)
    if (!force && state._loadedRange && state._loadedRange.date_from === range.date_from && state._loadedRange.date_to === range.date_to && state._fetchedOrgId === orgId) return;
    const showLoading = state.appointments.length === 0;
    set({ isLoading: showLoading, isRefreshing: true, _fetchedOrgId: orgId });
    try {
      const appointments = await appointmentsApi.list({ org_id: orgId, date_from: range.date_from, date_to: range.date_to });
      set({ appointments, isLoading: false, isRefreshing: false, _loadedRange: range });
    } catch (error) {
      console.error("Error fetching appointments:", error);
      set({ appointments: [], isLoading: false, isRefreshing: false, _fetchedOrgId: undefined, _loadedRange: undefined });
    }
  },

  setSelectedDate: (date) => {
    const state = get();
    set({ selectedDate: date });
    // Re-fetch if the new date falls outside the loaded week
    const newRange = getWeekRange(date);
    if (state._fetchedOrgId && (!state._loadedRange || state._loadedRange.date_from !== newRange.date_from)) {
      // Reset flags so fetchAppointments doesn't skip
      set({ _loadedRange: undefined, isRefreshing: false });
      get().fetchAppointments(state._fetchedOrgId);
    }
  },
  setViewMode: (mode) => set({ viewMode: mode }),
  setCompaction: (mode) => set({ compaction: mode }),
  toggleCategory: (cat) =>
    set((state) => ({
      activeCategories: state.activeCategories.includes(cat)
        ? state.activeCategories.filter((c) => c !== cat)
        : [...state.activeCategories, cat],
    })),
  setSearchQuery: (query) => set({ searchQuery: query }),
  setHighlightOrdreId: (id) => {
    set({ highlightOrdreId: id });
    if (id) setTimeout(() => set((s) => s.highlightOrdreId === id ? { highlightOrdreId: null } : {}), 5000);
  },

  updateAppointment: (id, updates) => {
    const appt = get().appointments.find((a) => a.id === id);
    // Réception & restitution must stay on the same reception column (= same person
    // in charge). When an ordre-linked appointment is moved to a different column,
    // move its sibling (same ordre_id, other leg) onto that column too.
    let siblingId: string | undefined;
    if (appt?.ordre_id && updates.category && updates.category !== appt.category) {
      const sibling = get().appointments.find(
        (a) => a.id !== id && a.ordre_id === appt.ordre_id && a.category !== updates.category,
      );
      siblingId = sibling?.id;
    }
    set((state) => ({
      appointments: state.appointments.map((a) => {
        if (a.id === id) return { ...a, ...updates };
        if (siblingId && a.id === siblingId) return { ...a, category: updates.category! };
        return a;
      }),
    }));
    appointmentsApi.update(id, updates as Partial<Appointment>).catch((e) => console.error("Error updating appointment:", e));
    if (siblingId) {
      appointmentsApi
        .update(siblingId, { category: updates.category } as Partial<Appointment>)
        .catch((e) => console.error("Error syncing sibling appointment:", e));
    }
  },

  createAppointment: async (data) => {
    try {
      const created = await appointmentsApi.create(data);
      set((state) => ({ appointments: [...state.appointments, created] }));
      return created;
    } catch (e) {
      console.error("Error creating appointment:", e);
      return null;
    }
  },

  getFilteredAppointments: () => {
    const state = get();
    return state.appointments.filter(
      (a) =>
        a.date === state.selectedDate &&
        state.activeCategories.includes(a.category) &&
        (state.searchQuery === "" ||
          a.clientName.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
          a.plate.toLowerCase().includes(state.searchQuery.toLowerCase()))
    );
  },
    }),
    {
      name: "planning-prefs",
      partialize: (state) => ({
        viewMode: state.viewMode,
        compaction: state.compaction,
        activeCategories: state.activeCategories,
      }),
    },
  ),
);
